import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import EChartBase from '../../../components/charts/EChartBase';
import { useRiskHistory } from '../hooks/useRiskHistory';

export const FIRMWIDE = 'FIRMWIDE';
export const STRATEGY_ORDER = [
  'MERGER ARBITRAGE',
  'EDFI',
  'SPAC LIQUIDATION ARBITRAGE',
  'CONVERTIBLE ARBITRAGE',
  'CREDIT ARBITRAGE',
  'VOL ARBITRAGE',
  'STAT ARBITRAGE',
];

export const RANGES = [
  { key: '1M', label: '1M', months: 1 },
  { key: '3M', label: '3M', months: 3 },
  { key: '6M', label: '6M', months: 6 },
  { key: '1Y', label: '1Y', months: 12 },
];

export const MODE_EXPOSURE = 'exposure';
export const MODE_PNL = 'pnl';

export const COLORS = {
  lmv: '#059669',
  smv: '#e05a5a',
  loss: '#dc2626',
  var95: '#d97706',
  var99: '#1e293b',
  grid: '#eef2f6',
  axis: '#94a3b8',
};

// $1.23B / $456M style, matching the rest of the page's magnitude formatting.
// A chart axis whose largest value is under this many dollars is shown in $K, not $M.
const K_SWITCH = 10000;
const maxAbs = (values) => values.reduce((m, v) => (v == null || !Number.isFinite(v) ? m : Math.max(m, Math.abs(v))), 0);

export const formatMagnitude = (value, inK = false) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (inK) return `$${(abs / 1e3).toFixed(abs < 1e3 ? 2 : 1).replace(/\.0$/, '')}K`;
  if (abs >= 1e9) return `$${(abs / 1e9).toFixed(2)}B`;
  const millions = abs / 1e6;
  const decimals = millions < 10 ? 2 : millions < 100 ? 1 : 0;
  return `$${millions.toFixed(decimals)}M`;
};
// Signed P&L: losses in brackets like elsewhere on the page.
export const formatSigned = (value, inK = false) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value < 0 ? `(${formatMagnitude(value, inK)})` : formatMagnitude(value, inK);
};
// Full-precision dollars for hover tooltips ($2,772,192); losses in brackets.
const formatFull = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const text = `$${Math.round(Math.abs(value)).toLocaleString('en-US')}`;
  return value < 0 ? `(${text})` : text;
};
// VaR is a loss: shown in brackets like elsewhere on the page.
export const formatVar = (value, inK = false) => (value === null || value === undefined ? '—' : `(${formatMagnitude(value, inK)})`);

// keepVarSign: strategy-level VaR is a strategy's P&L in the type's VaR scenario, so it can be positive;
// type/firmwide VaR is always shown as a loss (negative).
const buildOption = (points, compact, mode, keepVarSign = false) => {
  const isPnl = mode === MODE_PNL;
  const dates = points.map((p) => p.date);
  const fontSize = compact ? 10 : 11;
  // Exposures: loss magnitude on a right axis (VaR is ~0.1% of exposure). P&L: VaR as the negative
  // number it is, on the same axis as the daily P&L it is compared against.
  // VaR is always plotted as the negative (loss) number it is, so on the right axis zero is at the top, VaR 95%
  // sits below it and the more severe VaR 99% below that. Strategy-level VaR keeps its own sign.
  const varData = (key) => points.map((p) => (p[key] == null ? null : keepVarSign ? p[key] : -Math.abs(p[key])));
  const varAxis = isPnl ? 0 : 1;
  // Per-axis unit: $K when everything on that axis is under $10k, otherwise $M/$B.
  const varMax = maxAbs(points.flatMap((p) => [p.var_95, p.var_99]));
  const leftMax = isPnl
    ? Math.max(maxAbs(points.map((p) => p.net_pnl)), varMax)
    : maxAbs(points.map((p) => (p.lmv || 0) + (p.smv || 0)));
  const leftInK = leftMax < K_SWITCH;
  const varInK = isPnl ? leftInK : varMax < K_SWITCH;
  // Pin zero to the top of the VaR axis unless a (strategy-level) VaR is positive.
  const varHasPositive = keepVarSign && points.some((p) => (p.var_95 ?? 0) > 0 || (p.var_99 ?? 0) > 0);

  return {
    animationDuration: 400,
    grid: { left: 4, right: 4, top: compact ? 14 : 28, bottom: 0, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(148, 163, 184, 0.12)' } },
      backgroundColor: 'rgba(255, 255, 255, 0.97)',
      borderColor: '#e2e8f0',
      textStyle: { color: '#1e293b', fontSize: 12 },
      formatter: (params) => {
        if (!params?.length) return '';
        // NMV = LMV - SMV for the hovered day; listed under SMV in the exposure tooltip.
        const day = points[params[0].dataIndex];
        const nmv = day && day.lmv != null && day.smv != null ? day.lmv - day.smv : null;
        // Blank spacer the size of ECharts' colour dot (10px + 4px margin) so "NMV" lines up with LMV / SMV.
        const dotSpacer = '<span style="display:inline-block;width:10px;height:10px;margin-right:4px"></span>';
        const nmvRow = `<div style="display:flex;justify-content:space-between;gap:16px"><span>${dotSpacer}NMV</span><b>${formatSigned(nmv, leftInK)}</b></div>`;
        const rows = params
          .map((p) => {
            const isVar = p.seriesName.startsWith('VaR');
            // P&L and VaR hover at full precision; exposures keep the abbreviated form.
            const text = isPnl || isVar ? formatFull(p.value) : formatMagnitude(p.value, leftInK);
            const row = `<div style="display:flex;justify-content:space-between;gap:16px"><span>${p.marker}${p.seriesName}</span><b>${text}</b></div>`;
            return p.seriesName === 'SMV' && !isPnl ? row + nmvRow : row;
          })
          .join('');
        return `<div style="font-weight:600;margin-bottom:4px">${moment(params[0].axisValue).format('ddd, MMM D YYYY')}</div>${rows}`;
      },
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
      axisLabel: {
        color: COLORS.axis,
        fontSize,
        hideOverlap: true,
        formatter: (d) => moment(d).format('MMM D'),
      },
    },
    yAxis: [
      {
        type: 'value',
        axisLabel: { color: COLORS.axis, fontSize, formatter: (v) => formatSigned(v, leftInK).replace('.00', '') },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      ...(isPnl ? [] : [
      {
        type: 'value',
        position: 'right',
        ...(varHasPositive ? {} : { max: 0 }),
        axisLabel: { color: COLORS.var95, fontSize, formatter: (v) => (v === 0 ? formatMagnitude(0, varInK) : formatSigned(v, varInK)).replace('.00', '') },
        splitLine: { show: false },
      },
      ]),
    ],
    series: [
      ...(isPnl ? [
      {
        name: 'Net P&L',
        type: 'bar',
        barMaxWidth: compact ? 10 : 18,
        // Green for a gain, red for a loss, like the Daily P&L Movements chart.
        data: points.map((p) => ({
          value: p.net_pnl,
          itemStyle: { color: p.net_pnl < 0 ? COLORS.loss : COLORS.lmv, borderRadius: p.net_pnl < 0 ? [0, 0, 2, 2] : [2, 2, 0, 0] },
        })),
      },
      ] : [
      {
        name: 'LMV',
        type: 'bar',
        stack: 'exposure',
        barMaxWidth: compact ? 10 : 18,
        itemStyle: { color: COLORS.lmv },
        data: points.map((p) => p.lmv),
      },
      {
        name: 'SMV',
        type: 'bar',
        stack: 'exposure',
        barMaxWidth: compact ? 10 : 18,
        itemStyle: { color: COLORS.smv, borderRadius: [2, 2, 0, 0] },
        data: points.map((p) => p.smv),
      },
      ]),
      {
        name: 'VaR 95%',
        type: 'line',
        yAxisIndex: varAxis,
        smooth: 0.25,
        showSymbol: !compact && points.length <= 40,
        symbolSize: 5,
        lineStyle: { width: compact ? 1.75 : 2.25, color: COLORS.var95 },
        itemStyle: { color: COLORS.var95 },
        data: varData('var_95'),
      },
      {
        name: 'VaR 99%',
        type: 'line',
        yAxisIndex: varAxis,
        smooth: 0.25,
        showSymbol: !compact && points.length <= 40,
        symbolSize: 5,
        lineStyle: { width: compact ? 1.75 : 2.25, color: COLORS.var99, type: 'dashed' },
        itemStyle: { color: COLORS.var99 },
        data: varData('var_99'),
      },
    ],
  };
};

export const Legend = ({ mode }) => (
  <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
    {[
      ...(mode === MODE_PNL
        ? [
            { label: 'Net P&L gain', color: COLORS.lmv, shape: 'bar' },
            { label: 'Net P&L loss', color: COLORS.loss, shape: 'bar' },
          ]
        : [
            { label: 'LMV', color: COLORS.lmv, shape: 'bar' },
            { label: 'SMV', color: COLORS.smv, shape: 'bar' },
          ]),
      { label: mode === MODE_PNL ? 'VaR 95%' : 'VaR 95% (right axis)', color: COLORS.var95, shape: 'line' },
      { label: mode === MODE_PNL ? 'VaR 99%' : 'VaR 99% (right axis)', color: COLORS.var99, shape: 'dashed' },
    ].map(({ label, color, shape }) => (
      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {shape === 'bar' ? (
          <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: color }} />
        ) : (
          <Box sx={{ width: 16, borderTop: `2px ${shape === 'dashed' ? 'dashed' : 'solid'} ${color}` }} />
        )}
        <Typography sx={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>{label}</Typography>
      </Box>
    ))}
  </Box>
);

const Stat = ({ label, value, color }) => (
  <Box>
    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '13px', fontWeight: 700, color }}>{value}</Typography>
  </Box>
);

export const ChartCard = ({ title, points, mode, hero = false, onSelect, keepVarSign = false }) => {
  const option = useMemo(() => buildOption(points, !hero, mode, keepVarSign), [points, hero, mode, keepVarSign]);
  const fmtVar = keepVarSign ? formatSigned : formatVar;
  const latest = points[points.length - 1];
  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        p: hero ? 2.5 : 2,
        boxShadow: 'none',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Typography
          onClick={!hero && onSelect ? () => onSelect(title) : undefined}
          title={!hero && onSelect ? 'Show as main chart' : undefined}
          sx={{
            fontSize: hero ? '14px' : '12px',
            fontWeight: 700,
            color: '#1e293b',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            ...(!hero && onSelect && { cursor: 'pointer', '&:hover': { color: COLORS.var95, textDecoration: 'underline' } }),
          }}
        >
          {title === FIRMWIDE ? 'Firmwide' : title}
        </Typography>
        {latest && (
          <Box sx={{ display: 'flex', gap: hero ? 3 : 1.75 }}>
            {mode === MODE_PNL ? (
              <Stat label="Net P&L" value={formatSigned(latest.net_pnl)} color={latest.net_pnl < 0 ? COLORS.loss : COLORS.lmv} />
            ) : (
              <>
                <Stat label="LMV" value={formatMagnitude(latest.lmv)} color={COLORS.lmv} />
                <Stat label="SMV" value={formatMagnitude(latest.smv)} color={COLORS.smv} />
              </>
            )}
            <Stat label="VaR 95%" value={fmtVar(latest.var_95)} color={COLORS.var95} />
            {hero && <Stat label="VaR 99%" value={fmtVar(latest.var_99)} color={COLORS.var99} />}
          </Box>
        )}
      </Box>
      {/* notMerge: switching modes must replace the series, or ECharts keeps the old LMV/SMV bars (and their stack) alive. */}
      <EChartBase option={option} height={hero ? 340 : 210} emptyMessage="No history for this range" notMerge />
    </Box>
  );
};

export default function StrategyRiskAnalyticsDialog({ open, onClose, asOfDate }) {
  const [rangeKey, setRangeKey] = useState('1M');
  const [mode, setMode] = useState(MODE_PNL);
  const [focused, setFocused] = useState(FIRMWIDE);
  const handleClose = () => {
    setFocused(FIRMWIDE);
    onClose();
  };
  const months = RANGES.find((r) => r.key === rangeKey).months;
  const endDate = asOfDate ? moment(asOfDate).format('YYYY-MM-DD') : null;
  const startDate = endDate ? moment(endDate).subtract(months, 'months').format('YYYY-MM-DD') : null;

  const { data, isLoading, isError } = useRiskHistory(startDate, endDate, open);
  const series = data?.series || {};

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth PaperProps={{ sx: { backgroundColor: '#f8fafc' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Strategy Risk Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mode === MODE_PNL ? 'Daily net P&L' : 'Daily LMV / SMV'} with VaR 95% and VaR 99%{endDate ? `, through ${endDate}` : ''}.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ToggleButtonGroup value={mode} exclusive size="small" onChange={(e, next) => next && setMode(next)}>
              <ToggleButton value={MODE_PNL} sx={{ px: 1.5, py: 0.25, fontSize: '12px', fontWeight: 700 }}>
                P&amp;L
              </ToggleButton>
              <ToggleButton value={MODE_EXPOSURE} sx={{ px: 1.5, py: 0.25, fontSize: '12px', fontWeight: 700 }}>
                Exposures
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              value={rangeKey}
              exclusive
              size="small"
              onChange={(e, next) => next && setRangeKey(next)}
            >
              {RANGES.map((r) => (
                <ToggleButton key={r.key} value={r.key} sx={{ px: 1.5, py: 0.25, fontSize: '12px', fontWeight: 700 }}>
                  {r.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <IconButton onClick={handleClose} size="small" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <Legend mode={mode} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {isError && <Alert severity="error" sx={{ mb: 2 }}>Could not load risk history.</Alert>}
        {data && !data.var_available && (
          <Alert severity="warning" sx={{ mb: 2 }}>VaR history is unavailable (Risk Monitor could not be reached); showing exposures only.</Alert>
        )}
        {isLoading ? (
          <EChartBase option={null} loading height={340} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ChartCard key={focused} title={focused} points={series[focused] || []} mode={mode} hero />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              {[FIRMWIDE, ...STRATEGY_ORDER].filter((name) => name !== focused).map((name) => (
                <ChartCard key={name} title={name} points={series[name] || []} mode={mode} onSelect={setFocused} />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
